'use client';

import { useState } from 'react';
import { fetchActivitiesFromPB, fetchWellnessFromPB, fetchActivityMetricsFromPB } from '@/lib/pbSync';
import type { WellnessRecord, ActivityMetric } from '@/lib/pbSync';
import { ParsedFitResult } from '@/lib/fitParser';
import { RefreshCw, CheckCircle2 } from 'lucide-react';

interface Props {
  onSyncComplete?: (data: ParsedFitResult[]) => void;
  onWellnessSync?: (data: WellnessRecord[]) => void;
  onMetricsSync?: (data: ActivityMetric[]) => void;
}

export function PocketBaseSync({ onSyncComplete, onWellnessSync, onMetricsSync }: Props) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [status, setStatus] = useState<'idle' | 'done' | 'error'>('idle');
  const [counts, setCounts] = useState({ activities: 0, wellness: 0 });

  const handleSync = async () => {
    setIsSyncing(true);
    setStatus('idle');
    try {
      const [activities, wellness, metrics] = await Promise.all([
        fetchActivitiesFromPB(),
        fetchWellnessFromPB(),
        fetchActivityMetricsFromPB(),
      ]);

      setCounts({ activities: activities.length, wellness: wellness.length });
      setStatus('done');

      if (onSyncComplete && activities.length > 0) {
        activities.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
        onSyncComplete(activities);
      }
      if (onWellnessSync && wellness.length > 0) onWellnessSync(wellness);
      if (onMetricsSync  && metrics.length  > 0) onMetricsSync(metrics);

    } catch {
      setStatus('error');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-5 rounded-2xl bg-card border border-border/60">
      <div>
        <p className="text-sm font-semibold tracking-tight">Sync from PocketBase</p>
        <p className="text-xs text-muted-foreground mt-0.5">Pulls activities, wellness and sleep records from the local database.</p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={handleSync}
          disabled={isSyncing}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-foreground text-background text-xs font-medium hover:opacity-80 transition-opacity disabled:opacity-40"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
          {isSyncing ? 'Syncing…' : 'Sync now'}
        </button>

        {status === 'done' && (
          <div className="flex items-center gap-1.5 text-xs text-emerald-500 font-medium">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {counts.activities} activities · {counts.wellness} wellness records
          </div>
        )}

        {status === 'error' && (
          <span className="text-xs text-rose-500">Connection failed — check PocketBase is running.</span>
        )}
      </div>
    </div>
  );
}
